import styled from "@emotion/styled";
import { Stack, Tab, Tabs } from "@mui/material";
import { SecondLevelTab, SecondLevelTabs } from "Components/_Shared/SecondLevelTabs";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useEffect, useState } from "react";

enum FirstLevelMenuItem {
  ClientAnalysis = 0,
  FinancialAnalysis = 1,
  ClientResult = 2,
}

export enum AnalysisDetailSection {
  Family = 0,
  Goals = 1,
  InvestmentQuestionnaire = 2,
  Budget = 3,
  Assets = 4,
  Insurance = 5,
  GoalFulfillment = 6,
  AssetsAndContracts = 7,
  Modeling = 8,
  ResultDocuments = 9,
}

const CLIENT_ANALYSIS_SECTIONS: AnalysisDetailSection[] = [
  AnalysisDetailSection.Family,
  AnalysisDetailSection.Goals,
  AnalysisDetailSection.InvestmentQuestionnaire,
  AnalysisDetailSection.Budget,
  AnalysisDetailSection.Assets,
  AnalysisDetailSection.Modeling,
];

const FINANCIAL_ANALYSIS_SECTIONS: AnalysisDetailSection[] = [
  AnalysisDetailSection.GoalFulfillment,
  AnalysisDetailSection.Insurance,
  AnalysisDetailSection.AssetsAndContracts,
];

const CLIENT_RESULT_SECTIONS: AnalysisDetailSection[] = [AnalysisDetailSection.ResultDocuments];

const FirstLevelTabs = styled(Tabs)`
  height: 40px;
  min-height: unset;
  padding: ${(props) => props.theme.spacing(0, Spacing.S)};
  border-radius: ${(props) => props.theme.spacing(Spacing.M)};
  background: rgba(0, 0, 0, 0.05);
  max-width: fit-content;
  align-items: center;
  z-index: 10;

  & .MuiTabs-indicator {
    height: 32px;
    background: ${(props) => props.theme.colors.white};
    border-radius: ${(props) => props.theme.spacing(Spacing.S)};
    z-index: 100;
  }
`;

const FirstLevelTab = styled(Tab)`
  z-index: 1000;
  text-transform: none;
  height: 32px;
  min-height: unset;
  padding: ${(props) => props.theme.spacing(0, Spacing.L)};
  font-size: 14px;

  &.Mui-selected {
    color: ${(props) => props.theme.colors.textPrimary};
  }
`;

type Props = {
  readonly value: AnalysisDetailSection;
  readonly onChange: (value: AnalysisDetailSection) => void;
};

const getFirstLevelBySection = (section: AnalysisDetailSection) => {
  if (CLIENT_ANALYSIS_SECTIONS.includes(section)) return FirstLevelMenuItem.ClientAnalysis;
  if (FINANCIAL_ANALYSIS_SECTIONS.includes(section)) return FirstLevelMenuItem.FinancialAnalysis;
  if (CLIENT_RESULT_SECTIONS.includes(section)) return FirstLevelMenuItem.ClientResult;
  return FirstLevelMenuItem.ClientAnalysis;
};

const getInitSecondLevelTab = (firstLevelValue: FirstLevelMenuItem) => {
  switch (firstLevelValue) {
    case FirstLevelMenuItem.ClientAnalysis:
      return AnalysisDetailSection.Family;
    case FirstLevelMenuItem.FinancialAnalysis:
      return AnalysisDetailSection.GoalFulfillment;
    case FirstLevelMenuItem.ClientResult:
      return AnalysisDetailSection.ResultDocuments;
  }
};

export const MultilevelTabMenu: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useResource();

  const [firstLevelValue, setFirstLevelValue] = useState<FirstLevelMenuItem>(getFirstLevelBySection(value));
  const [secondLevelValue, setSecondLevelValue] = useState(value);

  useEffect(() => {
    setFirstLevelValue(getFirstLevelBySection(value));
    setSecondLevelValue(value);
  }, [value]);

  const clientAnalysisTabs = [
    {
      label: t(Resources.AnalysisDetail.TabMenu.Family),
      value: AnalysisDetailSection.Family,
    },
    {
      label: t(Resources.AnalysisDetail.TabMenu.Goals),
      value: AnalysisDetailSection.Goals,
    },
    {
      label: t(Resources.AnalysisDetail.TabMenu.InvestmentQuestionnaire),
      value: AnalysisDetailSection.InvestmentQuestionnaire,
    },
    {
      label: t(Resources.AnalysisDetail.TabMenu.Budget),
      value: AnalysisDetailSection.Budget,
    },
    {
      label: t(Resources.AnalysisDetail.TabMenu.Assets),
      value: AnalysisDetailSection.Assets,
    },
    {
      label: t(Resources.AnalysisDetail.TabMenu.Modeling),
      value: AnalysisDetailSection.Modeling,
    },
  ];

  const financialAnalysisTabs = [
    {
      label: t(Resources.AnalysisDetail.TabMenu.GoalsFulfillment),
      value: AnalysisDetailSection.GoalFulfillment,
    },
    {
      label: t(Resources.AnalysisDetail.TabMenu.Insurance),
      value: AnalysisDetailSection.Insurance,
    },
    {
      label: t(Resources.AnalysisDetail.TabMenu.WealthAndAgreements),
      value: AnalysisDetailSection.AssetsAndContracts,
    },
  ];

  const clientResultTabs = [
    {
      label: t(Resources.AnalysisDetail.TabMenu.ResultDocuments),
      value: AnalysisDetailSection.ResultDocuments,
    },
  ];

  return (
    <Stack spacing={Spacing.M}>
      <FirstLevelTabs
        value={firstLevelValue}
        onChange={(_, newFirstLevel) => {
          setFirstLevelValue(newFirstLevel);
          const newSecondLevelValue = getInitSecondLevelTab(newFirstLevel);

          setSecondLevelValue(newSecondLevelValue);
          onChange(newSecondLevelValue);
        }}
      >
        <FirstLevelTab
          label={t(Resources.AnalysisDetail.Tab.ClientAnalysis)}
          value={FirstLevelMenuItem.ClientAnalysis}
          tabIndex={firstLevelValue}
          disableRipple
        />
        <FirstLevelTab
          label={t(Resources.AnalysisDetail.Tab.FinancialAnalysis)}
          value={FirstLevelMenuItem.FinancialAnalysis}
          tabIndex={firstLevelValue}
          disableRipple
        />
        <FirstLevelTab
          label={t(Resources.AnalysisDetail.Tab.ClientResult)}
          value={FirstLevelMenuItem.ClientResult}
          tabIndex={firstLevelValue}
          disableRipple
        />
      </FirstLevelTabs>
      <SecondLevelTabs
        value={secondLevelValue}
        onChange={(_, value) => {
          setSecondLevelValue(value);
          onChange(value);
        }}
      >
        {firstLevelValue === FirstLevelMenuItem.ClientAnalysis &&
          clientAnalysisTabs.map((tab) => (
            <SecondLevelTab key={tab.value} label={tab.label} value={tab.value} tabIndex={secondLevelValue} />
          ))}
        {firstLevelValue === FirstLevelMenuItem.FinancialAnalysis &&
          financialAnalysisTabs.map((tab) => (
            <SecondLevelTab key={tab.value} label={tab.label} value={tab.value} tabIndex={secondLevelValue} />
          ))}
        {firstLevelValue === FirstLevelMenuItem.ClientResult &&
          clientResultTabs.map((tab) => <SecondLevelTab key={tab.value} label={tab.label} value={tab.value} tabIndex={secondLevelValue} />)}
      </SecondLevelTabs>
    </Stack>
  );
};
